/**
 * Local storage for working without a connection: a small IndexedDB wrapper.
 *
 * Four stores: "files" (documents kept as bytes), "reads" (the answers the logbook page needs, so it can be shown offline) and
 * "queue" (things the skipper recorded offline, waiting to be sent). IndexedDB, not the query
 * cache, because it survives closing the app and restarting the phone. Where IndexedDB is not
 * available the wrapper says so (`available()`), and the callers must not pretend to have saved.
 */

const DB_NAME = "seaslot-offline";
const DB_VERSION = 2;

export type StoreName = "reads" | "queue" | "meta" | "files";

let opened: Promise<IDBDatabase> | null = null;

export function available(): boolean {
  return typeof indexedDB !== "undefined" && indexedDB !== null;
}

function open(): Promise<IDBDatabase> {
  if (!available()) return Promise.reject(new Error("IndexedDB is not available"));
  if (!opened) {
    opened = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        // migrations go here: bump DB_VERSION and add a step for each version, never drop the queue
        if (!db.objectStoreNames.contains("reads")) db.createObjectStore("reads", { keyPath: "key" });
        if (!db.objectStoreNames.contains("queue")) db.createObjectStore("queue", { keyPath: "operationId" });
        if (!db.objectStoreNames.contains("meta")) db.createObjectStore("meta", { keyPath: "key" });
        // version 2: the files of the documents shown at an inspection (the queue and the reads are kept as they are)
        if (!db.objectStoreNames.contains("files")) db.createObjectStore("files", { keyPath: "key" });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => {
        opened = null;
        reject(req.error ?? new Error("Could not open the local database"));
      };
    });
  }
  return opened;
}

function run<T>(store: StoreName, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(store, mode);
        const req = fn(tx.objectStore(store));
        // resolve only when the transaction is complete: that is when the data is really stored
        tx.oncomplete = () => resolve(req.result);
        tx.onerror = () => reject(tx.error ?? req.error ?? new Error("Local storage failed"));
        tx.onabort = () => reject(tx.error ?? new Error("Local storage was refused (is the device storage full?)"));
      }),
  );
}

export const put = <T extends object>(store: StoreName, value: T): Promise<unknown> => run(store, "readwrite", (s) => s.put(value));
export const get = <T>(store: StoreName, key: string): Promise<T | undefined> => run<T | undefined>(store, "readonly", (s) => s.get(key));
export const remove = (store: StoreName, key: string): Promise<unknown> => run(store, "readwrite", (s) => s.delete(key));
export const all = <T>(store: StoreName): Promise<T[]> => run<T[]>(store, "readonly", (s) => s.getAll());
export const clearStore = (store: StoreName): Promise<unknown> => run(store, "readwrite", (s) => s.clear());

/** Ask the browser not to clear the data when space runs low (not granted everywhere). */
export async function requestPersistence(): Promise<boolean> {
  try {
    return (await navigator.storage?.persist?.()) ?? false;
  } catch {
    return false;
  }
}

/** For tests: forget the open connection so a fresh database can be used. */
export function resetForTests(): void {
  opened = null;
}
